import React, { useEffect, useId, useRef, useState } from 'react'
import NoTransparencyWarning from './NoTransparencyWarning'

const ORIGINAL_OPTION = {
  mode: 'original',
  label: 'Keep original',
  description: 'Use the uploaded artwork without changing its background.',
}

const SOLID_OPTIONS = [
  {
    mode: 'connected',
    label: 'Remove connected background',
    description: 'Remove the detected color only where it connects to the image edges.',
    recommended: true,
  },
  {
    mode: 'all',
    label: 'Remove matching color everywhere',
    description: 'Also remove the detected color inside the artwork. Review carefully before applying.',
  },
]

const AUTOMATIC_OPTION = {
  mode: 'automatic',
  label: 'Automatic background removal',
  description: 'Use the generated transparent version. Restore the original if the result needs more cleanup.',
}

function backgroundOptions(image) {
  const options = [ORIGINAL_OPTION]
  if (image.backgroundDetected) options.push(...SOLID_OPTIONS)
  if (image.backgroundRemovalMode === 'automatic') options.push(AUTOMATIC_OPTION)
  return options
}

function BackgroundReviewDialog({ image, onClose, onPreview, onApply }) {
  const activeMode = image.backgroundRemovalMode || 'original'
  const options = backgroundOptions(image)
  const [selectedMode, setSelectedMode] = useState(activeMode)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [previewError, setPreviewError] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(true)
  const [applying, setApplying] = useState(false)
  const [applyError, setApplyError] = useState(null)
  const titleId = useId()
  const descriptionId = useId()
  const dialogRef = useRef(null)
  const initialControlRef = useRef(null)
  const previewGenerationRef = useRef(0)
  const sourceName = image.originalFilename || 'Artwork'

  useEffect(() => {
    initialControlRef.current?.focus()
  }, [])

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape' && !applying) onClose()
      if (event.key !== 'Tab') return

      const focusable = Array.from(dialogRef.current?.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      ) || [])
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [applying, onClose])

  useEffect(() => {
    const generation = ++previewGenerationRef.current
    let objectUrl = null
    setPreviewLoading(true)
    setPreviewError(null)
    setPreviewUrl(null)

    Promise.resolve(onPreview?.(image.id, selectedMode))
      .then(blob => {
        if (previewGenerationRef.current !== generation) return
        objectUrl = URL.createObjectURL(blob)
        setPreviewUrl(objectUrl)
      })
      .catch(error => {
        if (previewGenerationRef.current !== generation) return
        setPreviewError(error?.message || 'Could not load this preview.')
      })
      .finally(() => {
        if (previewGenerationRef.current === generation) setPreviewLoading(false)
      })

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [image.id, onPreview, selectedMode])

  const handleApply = async () => {
    if (selectedMode === activeMode && image.backgroundProcessingStatus !== 'failed') {
      onClose()
      return
    }

    setApplying(true)
    setApplyError(null)
    try {
      await onApply?.(image.id, selectedMode)
      onClose()
    } catch (error) {
      setApplyError(error?.message || 'Could not update the artwork background.')
      setApplying(false)
    }
  }

  return (
    <div
      className="artwork-background-dialog__backdrop"
      onMouseDown={event => {
        if (event.target === event.currentTarget && !applying) onClose()
      }}
    >
      <section
        ref={dialogRef}
        className="artwork-background-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <div className="artwork-background-dialog__choices">
          <div>
            <h2 id={titleId}>Review background for {sourceName}</h2>
            <p id={descriptionId}>
              {image.backgroundDetected
                ? 'Choose how this background should be handled.'
                : 'Compare the generated transparent artwork with the original.'}
            </p>
          </div>

          <fieldset disabled={applying}>
            <legend className="sr-only">Background processing option</legend>
            {options.map((option, index) => (
              <label
                className={`artwork-background-dialog__option${selectedMode === option.mode ? ' artwork-background-dialog__option--selected' : ''}`}
                key={option.mode}
              >
                <input
                  ref={index === 0 ? initialControlRef : undefined}
                  type="radio"
                  name={`background-mode-${image.id}`}
                  value={option.mode}
                  checked={selectedMode === option.mode}
                  onChange={() => setSelectedMode(option.mode)}
                />
                <span>
                  <strong>{option.label}</strong>
                  <small>{option.description}</small>
                </span>
                {option.recommended && <em>Recommended</em>}
              </label>
            ))}
          </fieldset>

          {applyError && <p className="artwork-background-dialog__error" role="alert">{applyError}</p>}
          <div className="artwork-background-dialog__actions">
            <button
              className="gs-btn-primary"
              type="button"
              disabled={applying || previewLoading || Boolean(previewError)}
              onClick={handleApply}
            >
              {applying ? 'Applying…' : 'Apply'}
            </button>
            <button className="gs-btn-secondary" type="button" disabled={applying} onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>

        <div className="artwork-background-dialog__preview" aria-live="polite">
          {previewLoading && <span>Preparing preview…</span>}
          {previewError && <p role="alert">{previewError}</p>}
          {previewUrl && <img src={previewUrl} alt={`${selectedMode === 'original' ? 'Original' : 'Background removal'} preview for ${sourceName}`} />}
        </div>
      </section>
    </div>
  )
}

export default function ArtworkBackgroundControl({ image, onPreview, onApply }) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [automaticApplying, setAutomaticApplying] = useState(false)
  const [automaticError, setAutomaticError] = useState(null)
  const [originalApplying, setOriginalApplying] = useState(false)
  const [originalError, setOriginalError] = useState(null)
  const reviewButtonRef = useRef(null)
  const processing = image.backgroundProcessingStatus === 'processing'
  const failed = image.backgroundProcessingStatus === 'failed'
  const removed = Boolean(image.backgroundRemovalMode)
  const detected = image.backgroundDetected
  const sourceName = image.originalFilename || 'Artwork'

  useEffect(() => {
    setAutomaticError(null)
    setOriginalError(null)
  }, [image.backgroundProcessingStatus, image.backgroundRevision])

  const handleAutomaticRemoval = async () => {
    setAutomaticApplying(true)
    setAutomaticError(null)
    try {
      await onApply?.(image.id, 'automatic')
    } catch (error) {
      setAutomaticError(error?.message || 'Could not remove this background. Try again or keep the original.')
    } finally {
      setAutomaticApplying(false)
    }
  }

  const handleUseOriginal = async () => {
    setOriginalApplying(true)
    setOriginalError(null)
    try {
      await onApply?.(image.id, 'original')
    } catch (error) {
      setOriginalError(error?.message || 'Could not restore the original artwork. Please try again.')
    } finally {
      setOriginalApplying(false)
    }
  }

  if (processing) {
    return (
      <div className="artwork-background-notice artwork-background-notice--processing" role="status">
        <span className="artwork-background-notice__copy">
          <strong>Removing background…</strong>
          <small>You can keep configuring this design while we prepare it.</small>
        </span>
        <button
          type="button"
          disabled={originalApplying || !onApply}
          onClick={handleUseOriginal}
        >
          {originalApplying ? 'Using original…' : 'Use original artwork'}
        </button>
        {originalError && <span className="artwork-background-notice__error" role="alert">{originalError}</span>}
      </div>
    )
  }

  if (!detected && !removed && image.hasTransparency === false) {
    return (
      <NoTransparencyWarning
        actionLabel={automaticApplying ? 'Starting…' : failed ? 'Try again' : 'Make transparent'}
        actionAriaLabel={`${failed ? 'Retry making' : 'Make'} ${sourceName} transparent`}
        disabled={automaticApplying || originalApplying || !onApply}
        secondaryActionLabel={failed ? (originalApplying ? 'Using original…' : 'Use original artwork') : undefined}
        secondaryActionAriaLabel={failed ? `Use original artwork for ${sourceName}` : undefined}
        error={automaticError || originalError || (failed ? 'Automatic background removal failed. The original artwork is unchanged. Retry it or continue with the original.' : null)}
        onAction={handleAutomaticRemoval}
        onSecondaryAction={handleUseOriginal}
      />
    )
  }

  if (!detected && !removed && !failed) {
    return null
  }

  const closeDialog = () => {
    setDialogOpen(false)
    requestAnimationFrame(() => reviewButtonRef.current?.focus())
  }

  return (
    <>
      <div className={`artwork-background-notice artwork-background-notice--${failed ? 'error' : removed ? 'applied' : 'detected'}`}>
        <span>{failed ? 'Background removal failed' : removed ? 'Background removed' : 'Background detected'}</span>
        <button
          ref={reviewButtonRef}
          type="button"
          aria-label={`Review background for ${sourceName}`}
          onClick={() => setDialogOpen(true)}
        >
          Review
        </button>
        {failed && (
          <button
            type="button"
            aria-label={`Use original artwork for ${sourceName}`}
            disabled={originalApplying || !onApply}
            onClick={handleUseOriginal}
          >
            {originalApplying ? 'Using original…' : 'Use original artwork'}
          </button>
        )}
        {originalError && <span className="artwork-background-notice__error" role="alert">{originalError}</span>}
      </div>
      {dialogOpen && (
        <BackgroundReviewDialog
          image={image}
          onClose={closeDialog}
          onPreview={onPreview}
          onApply={onApply}
        />
      )}
    </>
  )
}
